import { isAxiosError } from 'axios'
import { apiService } from './api'
import type {
  CopyOrganizationBudgetsPayload,
  DashboardPagesLeaderboardData,
  DashboardPagesMetricsEnvelope,
  DashboardPagesOpenPipelineData,
  DashboardPagesOpenPipelinePipelinesData,
  DashboardPagesPerformanceData,
  DashboardPagesPerformanceFilterOptionsData,
  DashboardPagesAmazonDashboardData,
  DashboardPagesSaleDetailData,
  SalesDashboardBudgetCopyMeta,
  SalesDashboardBudgetEnvelope,
  SalesDashboardBudgetErrorEnvelope,
  SalesDashboardBudgetImportEnvelope,
  SalesDashboardBudgetImportErrorEnvelope,
  SalesDashboardBudgetImportResult,
  SalesDashboardBudgetYearData,
  SalesTeamsRosterData,
  SalesTeamsRosterEnvelope,
  UnifiedSalesBudgetImportResult,
  SalesRepBudgetEnvelope,
  SalesRepBudgetImportEnvelope,
  SalesRepBudgetImportErrorEnvelope,
  SalesRepBudgetImportResult,
  SalesRepBudgetPeriodData,
  UpdateSalesRepBudgetPayload,
  UpdateOrganizationBudgetsPayload,
} from '@/types/salesDashboardPages.api'
import {
  serializeDashboardPagesQueryParams,
  type DashboardPagesQueryParams,
} from '@/utils/salesDashboardPagesRequest'
import type { InboundOrdersPayload, InboundOrdersSearchParams } from '@/types/inboundOrders'

export type SalesDashboardPagesRequestResult<T> =
  | { ok: true; data: T; meta?: SalesDashboardBudgetCopyMeta; message?: string }
  | {
      ok: false
      status: number
      message: string
      errors?: Record<string, string[]>
      errorList?: string[]
      code?: string
    }

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object'
}

function extractErrorPayload(data: unknown): SalesDashboardBudgetErrorEnvelope {
  if (!isRecord(data)) {
    return {}
  }
  return {
    status: typeof data.status === 'string' ? data.status : undefined,
    message: typeof data.message === 'string' ? data.message : undefined,
    code: typeof data.code === 'string' ? data.code : undefined,
    errors: isRecord(data.errors)
      ? Object.fromEntries(
          Object.entries(data.errors).map(([key, value]) => [
            key,
            Array.isArray(value) ? value.map(String) : [String(value)],
          ])
        )
      : undefined,
  }
}

function toErrorResult(status: number, data: unknown, fallback: string): SalesDashboardPagesRequestResult<never> {
  const payload = extractErrorPayload(data)
  return {
    ok: false,
    status,
    message: payload.message ?? fallback,
    errors: payload.errors,
    code: payload.code,
  }
}

function extractImportErrorList(data: unknown): string[] | undefined {
  if (!isRecord(data)) {
    return undefined
  }
  const envelope = data as SalesDashboardBudgetImportErrorEnvelope
  if (Array.isArray(envelope.errors)) {
    return envelope.errors.map(String)
  }
  return undefined
}

function toImportErrorResult(
  status: number,
  data: unknown,
  fallback: string
): SalesDashboardPagesRequestResult<never> {
  if (!isRecord(data)) {
    return { ok: false, status, message: fallback }
  }

  const message =
    typeof data.message === 'string' ? data.message : fallback
  const errorList = extractImportErrorList(data)
  const payload = extractErrorPayload(data)

  return {
    ok: false,
    status,
    message,
    errorList,
    errors: payload.errors,
    code: payload.code,
  }
}

function getAxiosInstance() {
  return (apiService as unknown as { api: import('axios').AxiosInstance }).api
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function parseDownloadFilename(contentDisposition: string | undefined, fallback: string): string {
  if (!contentDisposition) {
    return fallback
  }
  const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
  if (filenameMatch?.[1]) {
    return filenameMatch[1].replace(/['"]/g, '')
  }
  return fallback
}

function isAbortError(error: unknown): boolean {
  return isAxiosError(error) && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError')
}

const DASHBOARD_PAGES_METRICS_BASE = '/sales-history/dashboard-pages'

async function fetchDashboardPagesMetrics<T>(
  path: string,
  params: DashboardPagesQueryParams,
  signal: AbortSignal | undefined,
  fallbackMessage: string
): Promise<SalesDashboardPagesRequestResult<T>> {
  try {
    const response = await apiService.getWithResponse<DashboardPagesMetricsEnvelope<T>>(
      `${DASHBOARD_PAGES_METRICS_BASE}${path}`,
      {
        params,
        paramsSerializer: serializeDashboardPagesQueryParams,
        signal,
      }
    )
    return { ok: true, data: response.data.data }
  } catch (error) {
    if (isAbortError(error)) {
      return { ok: false, status: 0, message: 'Request aborted.' }
    }
    if (isAxiosError(error) && error.response) {
      return toErrorResult(error.response.status, error.response.data, fallbackMessage)
    }
    return { ok: false, status: 0, message: fallbackMessage }
  }
}

export async function getDashboardPagesLeaderboard(
  params: DashboardPagesQueryParams,
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<DashboardPagesLeaderboardData>> {
  return fetchDashboardPagesMetrics(
    '/leaderboard',
    params,
    signal,
    'Failed to load leaderboard data.'
  )
}

export async function getDashboardPagesPerformance(
  params: DashboardPagesQueryParams,
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<DashboardPagesPerformanceData>> {
  return fetchDashboardPagesMetrics(
    '/performance',
    params,
    signal,
    'Failed to load performance data.'
  )
}

export async function getDashboardPagesPerformanceFilterOptions(
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<DashboardPagesPerformanceFilterOptionsData>> {
  try {
    const response = await apiService.getWithResponse<
      DashboardPagesMetricsEnvelope<DashboardPagesPerformanceFilterOptionsData>
    >(`${DASHBOARD_PAGES_METRICS_BASE}/filter-options`, { signal })
    return { ok: true, data: response.data.data }
  } catch (error) {
    if (isAbortError(error)) {
      return { ok: false, status: 0, message: 'Request aborted.' }
    }
    if (isAxiosError(error) && error.response) {
      return toErrorResult(
        error.response.status,
        error.response.data,
        'Failed to load performance filter options.'
      )
    }
    return { ok: false, status: 0, message: 'Failed to load performance filter options.' }
  }
}

export async function getDashboardPagesSaleDetail(
  params: DashboardPagesQueryParams,
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<DashboardPagesSaleDetailData>> {
  return fetchDashboardPagesMetrics(
    '/sale-detail',
    params,
    signal,
    'Failed to load sale detail data.'
  )
}

/** Fetch Amazon dashboard metrics and scoped sale records. */
export async function getDashboardPagesAmazonDashboard(
  params: DashboardPagesQueryParams,
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<DashboardPagesAmazonDashboardData>> {
  return fetchDashboardPagesMetrics(
    '/amazon-dashboard',
    params,
    signal,
    'Failed to load Amazon dashboard data.'
  )
}

export async function getDashboardPagesOpenPipelinePipelines(
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<DashboardPagesOpenPipelinePipelinesData>> {
  return fetchDashboardPagesMetrics(
    '/open-pipeline/pipelines',
    {},
    signal,
    'Failed to load HubSpot pipelines.'
  )
}

export async function getDashboardPagesOpenPipeline(
  params: DashboardPagesQueryParams,
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<DashboardPagesOpenPipelineData>> {
  return fetchDashboardPagesMetrics(
    '/open-pipeline',
    params,
    signal,
    'Failed to load open pipeline data.'
  )
}

/** Fetch inbound orders dashboard payload (quotes + accepted unshipped orders). */
export async function getInboundOrders(
  search: InboundOrdersSearchParams = {},
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<InboundOrdersPayload>> {
  const params: DashboardPagesQueryParams = {}
  if (search.quote_q?.trim()) params.quote_q = search.quote_q.trim()
  if (search.job_q?.trim()) params.job_q = search.job_q.trim()

  return fetchDashboardPagesMetrics(
    '/inbound-orders',
    params,
    signal,
    'Failed to load inbound orders data.'
  )
}

export async function getOrganizationBudgets(
  year: number,
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<SalesDashboardBudgetYearData>> {
  try {
    const response = await apiService.getWithResponse<SalesDashboardBudgetEnvelope>(
      `/v1/sales-dashboard-pages/budgets?year=${encodeURIComponent(String(year))}`,
      { signal }
    )
    return { ok: true, data: response.data.data }
  } catch (error) {
    if (isAbortError(error)) {
      return { ok: false, status: 0, message: 'Request aborted.' }
    }
    if (isAxiosError(error) && error.response) {
      return toErrorResult(
        error.response.status,
        error.response.data,
        'Failed to load organization budgets.'
      )
    }
    return { ok: false, status: 0, message: 'Failed to load organization budgets.' }
  }
}

export async function updateOrganizationBudgets(
  payload: UpdateOrganizationBudgetsPayload
): Promise<SalesDashboardPagesRequestResult<SalesDashboardBudgetYearData>> {
  try {
    const response = await apiService.put<SalesDashboardBudgetEnvelope>(
      '/v1/sales-dashboard-pages/budgets',
      payload
    )
    return { ok: true, data: response.data }
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return toErrorResult(
        error.response.status,
        error.response.data,
        'Failed to save organization budgets.'
      )
    }
    return { ok: false, status: 0, message: 'Failed to save organization budgets.' }
  }
}

export async function copyOrganizationBudgets(
  payload: CopyOrganizationBudgetsPayload
): Promise<SalesDashboardPagesRequestResult<SalesDashboardBudgetYearData>> {
  try {
    const response = await apiService.post<SalesDashboardBudgetEnvelope>(
      '/v1/sales-dashboard-pages/budgets/copy-year',
      payload
    )
    return { ok: true, data: response.data, meta: response.meta }
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return toErrorResult(
        error.response.status,
        error.response.data,
        'Failed to copy organization budgets.'
      )
    }
    return { ok: false, status: 0, message: 'Failed to copy organization budgets.' }
  }
}

export async function exportSalesBudgets(anchorYear?: number): Promise<void> {
  const axiosInstance = getAxiosInstance()
  const year = anchorYear ?? new Date().getFullYear()
  const fallbackFilename = `sales-budgets-${year - 1}-${year}-${year + 1}.csv`
  const query =
    anchorYear != null
      ? `?anchor_year=${encodeURIComponent(String(anchorYear))}`
      : ''

  const response = await axiosInstance.get(
    `/v1/sales-dashboard-pages/budgets/export${query}`,
    {
      responseType: 'blob',
    }
  )

  await assertBlobDownloadOk(response)

  const contentDisposition =
    (response.headers['content-disposition'] as string | undefined) ??
    (response.headers['Content-Disposition'] as string | undefined)
  const filename = parseDownloadFilename(contentDisposition, fallbackFilename)
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  triggerBlobDownload(blob, filename)
}

/** @deprecated Use exportSalesBudgets for unified org + rep export */
export async function exportOrganizationBudgets(year: number): Promise<void> {
  await exportSalesBudgets(year)
}

async function assertBlobDownloadOk(response: {
  status: number
  data: Blob
  headers: Record<string, unknown>
}): Promise<void> {
  if (response.status >= 400) {
    const text = await response.data.text()
    let message: string | undefined
    try {
      const parsed = JSON.parse(text) as { message?: string }
      message = parsed.message
    } catch {
      message = text || undefined
    }
    throw new Error(message ?? `Export failed (${response.status})`)
  }
  const contentType = String(response.headers['content-type'] ?? '').toLowerCase()
  if (contentType.includes('application/json')) {
    const text = await response.data.text()
    let message: string | undefined
    try {
      const parsed = JSON.parse(text) as { message?: string }
      message = parsed.message
    } catch {
      message = text || undefined
    }
    throw new Error(message ?? 'Export failed')
  }
}

export async function exportPerformanceDashboardCsv(
  params: DashboardPagesQueryParams
): Promise<void> {
  const axiosInstance = getAxiosInstance()
  const queryString = serializeDashboardPagesQueryParams(params)
  const period = String(params.period ?? 'mtd')
  const source = String(params.source ?? 'created')
  const fallbackFilename = `performance-${source}-${period}.csv`

  const response = await axiosInstance.get(
    `${DASHBOARD_PAGES_METRICS_BASE}/performance/export-csv?${queryString}`,
    {
      responseType: 'blob',
      timeout: 1_800_000,
    }
  )

  await assertBlobDownloadOk(response)

  const contentDisposition =
    (response.headers['content-disposition'] as string | undefined) ??
    (response.headers['Content-Disposition'] as string | undefined)
  const filename = parseDownloadFilename(contentDisposition, fallbackFilename)
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  triggerBlobDownload(blob, filename)
}

export async function exportLeaderboardDashboardCsv(
  params: DashboardPagesQueryParams
): Promise<void> {
  const axiosInstance = getAxiosInstance()
  const queryString = serializeDashboardPagesQueryParams(params)
  const source = String(params.source ?? 'created')
  const fallbackFilename = `leaderboard-${source}.csv`

  const response = await axiosInstance.get(
    `${DASHBOARD_PAGES_METRICS_BASE}/leaderboard/export-csv?${queryString}`,
    {
      responseType: 'blob',
      timeout: 1_800_000,
    }
  )

  await assertBlobDownloadOk(response)

  const contentDisposition =
    (response.headers['content-disposition'] as string | undefined) ??
    (response.headers['Content-Disposition'] as string | undefined)
  const filename = parseDownloadFilename(contentDisposition, fallbackFilename)
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  triggerBlobDownload(blob, filename)
}

export async function exportSaleDetailDashboardCsv(
  params: DashboardPagesQueryParams
): Promise<void> {
  const axiosInstance = getAxiosInstance()
  const queryString = serializeDashboardPagesQueryParams(params)
  const source = String(params.source ?? 'created')
  const fallbackFilename = `sale-detail-${source}.csv`

  const response = await axiosInstance.get(
    `${DASHBOARD_PAGES_METRICS_BASE}/sale-detail/export-csv?${queryString}`,
    {
      responseType: 'blob',
      timeout: 1_800_000,
    }
  )

  await assertBlobDownloadOk(response)

  const contentDisposition =
    (response.headers['content-disposition'] as string | undefined) ??
    (response.headers['Content-Disposition'] as string | undefined)
  const filename = parseDownloadFilename(contentDisposition, fallbackFilename)
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  triggerBlobDownload(blob, filename)
}

export async function exportAmazonDashboardCsv(
  params: DashboardPagesQueryParams
): Promise<void> {
  const axiosInstance = getAxiosInstance()
  const queryString = serializeDashboardPagesQueryParams(params)
  const source = String(params.source ?? 'created')
  const fallbackFilename = `amazon-dashboard-${source}.csv`

  const response = await axiosInstance.get(
    `${DASHBOARD_PAGES_METRICS_BASE}/amazon-dashboard/export-csv?${queryString}`,
    {
      responseType: 'blob',
      timeout: 1_800_000,
    }
  )

  await assertBlobDownloadOk(response)

  const contentDisposition =
    (response.headers['content-disposition'] as string | undefined) ??
    (response.headers['Content-Disposition'] as string | undefined)
  const filename = parseDownloadFilename(contentDisposition, fallbackFilename)
  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
  triggerBlobDownload(blob, filename)
}

export async function importSalesBudgets(
  file: File
): Promise<SalesDashboardPagesRequestResult<UnifiedSalesBudgetImportResult>> {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const axiosInstance = getAxiosInstance()
    const response = await axiosInstance.post<SalesDashboardBudgetImportEnvelope>(
      '/v1/sales-dashboard-pages/budgets/import',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 1_800_000,
      }
    )

    return {
      ok: true,
      data: response.data.data,
      message: response.data.message,
    }
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return toImportErrorResult(
        error.response.status,
        error.response.data,
        'Failed to import sales budgets.'
      )
    }
    return { ok: false, status: 0, message: 'Failed to import sales budgets.' }
  }
}

/** @deprecated Use importSalesBudgets for unified org + rep import */
export async function importOrganizationBudgets(
  file: File
): Promise<SalesDashboardPagesRequestResult<UnifiedSalesBudgetImportResult>> {
  return importSalesBudgets(file)
}

export async function getSalesRepBudgets(
  year: number,
  month: number,
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<SalesRepBudgetPeriodData>> {
  try {
    const response = await apiService.getWithResponse<SalesRepBudgetEnvelope>(
      `/v1/sales-dashboard-pages/budgets/reps?year=${encodeURIComponent(String(year))}&month=${encodeURIComponent(String(month))}`,
      { signal }
    )
    return { ok: true, data: response.data.data }
  } catch (error) {
    if (isAbortError(error)) {
      return { ok: false, status: 0, message: 'Request aborted.' }
    }
    if (isAxiosError(error) && error.response) {
      return toErrorResult(
        error.response.status,
        error.response.data,
        'Failed to load sales rep budgets.'
      )
    }
    return { ok: false, status: 0, message: 'Failed to load sales rep budgets.' }
  }
}

export async function updateSalesRepBudget(
  payload: UpdateSalesRepBudgetPayload
): Promise<SalesDashboardPagesRequestResult<SalesRepBudgetPeriodData>> {
  try {
    const response = await apiService.put<SalesRepBudgetEnvelope>(
      '/v1/sales-dashboard-pages/budgets/reps',
      payload
    )
    return { ok: true, data: response.data }
  } catch (error) {
    if (isAxiosError(error) && error.response) {
      return toErrorResult(
        error.response.status,
        error.response.data,
        'Failed to save sales rep budget.'
      )
    }
    return { ok: false, status: 0, message: 'Failed to save sales rep budget.' }
  }
}

function toRepBudgetImportErrorResult(
  status: number,
  data: SalesRepBudgetImportErrorEnvelope | SalesDashboardBudgetImportErrorEnvelope,
  fallback: string
): Extract<SalesDashboardPagesRequestResult<never>, { ok: false }> {
  const errorList = Array.isArray(data.errors)
    ? data.errors
    : data.errors
      ? Object.values(data.errors).flat()
      : undefined

  return {
    ok: false,
    status,
    message: data.message ?? fallback,
    errors: !Array.isArray(data.errors) ? data.errors : undefined,
    errorList,
  }
}

export async function getSalesTeamsRoster(
  signal?: AbortSignal
): Promise<SalesDashboardPagesRequestResult<SalesTeamsRosterData>> {
  try {
    const response = await apiService.getWithResponse<SalesTeamsRosterEnvelope>(
      '/v1/sales-dashboard-pages/teams-and-reps',
      { signal }
    )
    return { ok: true, data: response.data.data }
  } catch (error) {
    if (isAbortError(error)) {
      return { ok: false, status: 0, message: 'Request aborted.' }
    }
    if (isAxiosError(error) && error.response) {
      return toErrorResult(
        error.response.status,
        error.response.data,
        'Failed to load sales teams.'
      )
    }
    return { ok: false, status: 0, message: 'Failed to load sales teams.' }
  }
}
